import React, { useEffect, useState, useRef, useMemo } from "react";
import DataTable from "react-data-table-component";
import {
    AiOutlineCloudUpload,
    AiOutlineDelete,
    AiOutlineDownload,
    AiOutlineEdit,
    AiOutlineClose,
    AiOutlineUpload,
    AiOutlineSearch
} from "react-icons/ai";

// These will be populated from API
let YEARS = [];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

// Allowed file extensions (updated to include all required types)
const ALLOWED_FILE_EXTENSIONS = [
    '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.txt',
    '.png', '.jpeg', '.jpg', '.svg', '.heic', '.heif'
];

// Allowed MIME types
const ALLOWED_MIME_TYPES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/plain',
    'image/png',
    'image/jpeg',
    'image/jpg',
    'image/svg+xml',
    'image/heic',
    'image/heif'
];

function validateFile(file) {
    const errors = [];
    
    // Check file size
    if (file.size > MAX_FILE_SIZE) {
        errors.push(`File size must be less than 5MB. Current size: ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
    }
    
    // Check file extension
    const fileName = file.name.toLowerCase();
    const fileExtension = ALLOWED_FILE_EXTENSIONS.find(ext => fileName.endsWith(ext));
    
    if (!fileExtension) {
        errors.push(`Invalid file type. Allowed types: PDF, DOC, DOCX, Excel (XLS, XLSX), Images (PNG, JPEG, SVG, HEIC), TXT`);
    }
    
    // Check MIME type (optional - extension is primary check)
    if (file.type && !ALLOWED_MIME_TYPES.includes(file.type.toLowerCase())) {
        // For images, allow if extension is valid even if MIME type is different
        if (fileExtension && ['.png', '.jpeg', '.jpg', '.svg', '.heic', '.heif'].includes(fileExtension)) {
            if (!file.type.startsWith('image/')) {
                // Still allow if extension is valid (browser might send wrong MIME)
            }
        } else if (fileExtension) {
            // For documents, be more lenient if extension is valid
            // Don't add error if extension is valid
        }
    }
    
    return {
        isValid: errors.length === 0,
        errors: errors
    };
}

// API Configuration
import { API_CONFIG } from '../config/env';
const API_BASE_URL = API_CONFIG.BASE_URL;

// API Functions
async function apiGetYears() {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_BASE_URL}/documents/years/`, {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    });
    
    if (!response.ok) {
        throw new Error('Failed to fetch years');
    }
    
    const data = await response.json();
    return data.results || data || [];
}

async function apiGetAmendments() {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_BASE_URL}/documents/amendments/`, {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    });
    
    if (!response.ok) {
        throw new Error('Failed to fetch amendments');
    }
    
    const data = await response.json();
    return data.results || data || [];
}

async function apiUploadAmendment(formData) {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_BASE_URL}/documents/amendments/upload/`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
        },
        body: formData
    });
    
    if (!response.ok) {
        const errorData = await response.json();
        console.error('Upload error details:', errorData);
        throw new Error(errorData.error || errorData.detail || 'Upload failed');
    }
    
    const data = await response.json();
    return data;
}

async function apiUpdateAmendment(amendmentId, updateData) {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_BASE_URL}/documents/amendments/${amendmentId}/`, {
        method: 'PATCH',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData)
    });
    
    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.detail || 'Update failed');
    }
    
    return await response.json();
}

async function apiDeleteAmendment(amendmentId) {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_BASE_URL}/documents/amendments/${amendmentId}/`, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${token}`,
        }
    });
    
    if (!response.ok) {
        throw new Error('Failed to delete amendment');
    }
    
    return true;
}

async function apiDownloadAmendment(amendmentId) {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_BASE_URL}/documents/amendments/${amendmentId}/download/`, {
        headers: {
            'Authorization': `Bearer ${token}`,
        }
    });
    
    if (!response.ok) {
        throw new Error('Failed to download amendment');
    }
    
    return response;
}

export default function Amendment() {
    // Form inputs state
    const [category, setCategory] = useState("");  // Amendment or Notices (mandatory)
    const [year, setYear] = useState("");
    const [file, setFile] = useState(null);
    const [fileError, setFileError] = useState("");
    const [showValidationErrors, setShowValidationErrors] = useState(false);

    // Document data and loading
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    
    // Years from API
    const [years, setYears] = useState([]);
    const [yearsLoading, setYearsLoading] = useState(true);

    // Search term
    const [searchTerm, setSearchTerm] = useState("");

    // Pagination & sorting
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortColumn, setSortColumn] = useState(null);
    const [sortDirection, setSortDirection] = useState("asc");

    // Modals and edit form data
    const [editingDoc, setEditingDoc] = useState(null);
    const [editYear, setEditYear] = useState("");
    const [editFile, setEditFile] = useState(null);
    const [editFileError, setEditFileError] = useState("");
    const editFileInput = useRef();
    const [completedYears, setCompletedYears] = useState(new Set());

    const [deleteDoc, setDeleteDoc] = useState(null);

    // Refs for inputs
    const fileInput = useRef();

    // Fetch years on mount
    useEffect(() => {
        async function fetchYears() {
            setYearsLoading(true);
            try {
                const yearsData = await apiGetYears();
                setYears(yearsData);
            } catch (error) {
                console.error('Failed to fetch years:', error);
                setYears([]);
            }
            setYearsLoading(false);
        }
        fetchYears();
    }, []);

    // Fetch amendments on mount
    useEffect(() => {
        async function fetchAmendments() {
            setLoading(true);
            try {
                const amendments = await apiGetAmendments();
                setData(amendments);
                
                // Check for completed amendments - only years where ALL amendments have status "completed"
                // Allow multiple uploads for the same year until status is "completed"
                const completedYearsSet = new Set();
                
                // Group amendments by year
                const amendmentsByYear = {};
                amendments.forEach(amendment => {
                    const year = amendment.year;
                    if (!amendmentsByYear[year]) {
                        amendmentsByYear[year] = [];
                    }
                    amendmentsByYear[year].push(amendment);
                });
                
                // Check if all amendments for a year have status "completed"
                Object.keys(amendmentsByYear).forEach(year => {
                    const yearAmendments = amendmentsByYear[year];
                    // Only mark as completed if ALL amendments for that year have status "completed"
                    const allCompleted = yearAmendments.every(amendment => 
                        amendment.status === 'completed'
                    );
                    if (allCompleted && yearAmendments.length > 0) {
                        completedYearsSet.add(parseInt(year));
                        console.log(`Year ${year} marked as completed (all ${yearAmendments.length} amendments have status 'completed')`);
                    } else {
                        // Debug: log why year is not completed
                        const statuses = yearAmendments.map(a => `${a.year}:${a.status}`);
                        console.log(`Year ${year} NOT completed. Amendment statuses: ${statuses.join(', ')}`);
                    }
                });
                
                console.log('Completed years set:', Array.from(completedYearsSet));
                
                setCompletedYears(completedYearsSet);
            } catch (error) {
                console.error('Failed to fetch amendments:', error);
                setData([]);
            }
            setLoading(false);
        }
        fetchAmendments();
    }, [years]);

    // Filtered & searched data before pagination & display
    const filteredData = useMemo(() => {
        try {
            if (!data || !Array.isArray(data)) return [];
            if (!searchTerm) return data;
            const term = searchTerm.toLowerCase();
            return data.filter(
                (doc) => {
                    if (!doc) return false;
                    // Search by filename and year
                    const filenameMatch = doc.filename && typeof doc.filename === 'string' && doc.filename.toLowerCase().includes(term);
                    const yearMatch = doc.year && String(doc.year).includes(term);
                    return filenameMatch || yearMatch;
                }
            );
        } catch (error) {
            console.error('Error filtering amendments:', error);
            return data || [];
        }
    }, [data, searchTerm]);

    // Sort data on client side based on sortColumn and direction
    const sortedData = useMemo(() => {
        if (!sortColumn) return filteredData;
        const sortFn = (a, b) => {
            let aVal =
                typeof sortColumn === "function" ? sortColumn(a) : a[sortColumn];
            let bVal =
                typeof sortColumn === "function" ? sortColumn(b) : b[sortColumn];
            aVal = aVal === null || aVal === undefined ? "" : aVal;
            bVal = bVal === null || bVal === undefined ? "" : bVal;
            if (typeof aVal === "string") aVal = aVal.toLowerCase();
            if (typeof bVal === "string") bVal = bVal.toLowerCase();

            if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
            if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
            return 0;
        };

        return [...filteredData].sort(sortFn);
    }, [filteredData, sortColumn, sortDirection]);

    // Paginated rows for current page
    const paginatedRows = useMemo(() => {
        const start = (currentPage - 1) * perPage;
        return sortedData.slice(start, start + perPage);
    }, [sortedData, currentPage, perPage]);

    // Columns for react-data-table-component
    const columns = useMemo(() => [
        {
            name: "S.No",
            width: "80px",
            cell: (_row, idx) => (currentPage - 1) * perPage + idx + 1,
            ignoreRowClick: true,
            sortable: false,
            style: { fontWeight: 600, color: "#4338ca" },
        },
        {
            name: "Category",
            selector: (row) => row.category_display || (row.category === 'amendment' ? 'Amendment' : row.category === 'notices' ? 'Notices' : 'Amendment'),
            sortable: true,
            style: { fontWeight: 500, color: "#4338ca" },
            wrap: true,
        },
        {
            name: "Year",
            selector: (row) => row.year,
            sortable: true,
            width: "90px",
            style: { justifyContent: "center", display: "flex" },
        },
        {
            name: "Filename",
            selector: (row) => row.filename,
            sortable: true,
            cell: (row) => (
                <button
                    onClick={() => handleDownload(row)}
                    style={{
                        color: "#256bb7",
                        textDecoration: "underline",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        fontWeight: 600,
                    }}
                    title={`Download ${row.filename}`}
                    disabled={loading}
                >
                    <AiOutlineDownload style={{ verticalAlign: "middle", marginRight: 4 }} />
                    {row.filename}
                </button>
            ),
            wrap: true,
        },
        {
            name: "Datetime",
            selector: (row) => row.datetime,
            sortable: true,
            width: "180px",
            style: { color: "#6b7280", fontSize: 13 },
            cell: (row) => {
                if (!row.datetime) return 'N/A';
                try {
                    const date = new Date(row.datetime);
                    const cstDate = new Date(date.toLocaleString("en-US", {timeZone: "America/Chicago"}));
                    return cstDate.toLocaleString('en-US', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                        hour12: false,
                        timeZone: 'America/Chicago'
                    }) + ' CST';
                } catch (error) {
                    return row.datetime;
                }
            },
            wrap: true,
        },
        {
            name: "Actions",
            cell: (row) => (
                <div style={{ display: "flex", gap: 8 }}>
                    <button
                        onClick={() => openEdit(row)}
                        aria-label={`Edit amendment ${row.filename}`}
                        style={buttonStyle("#4338ca")}
                    >
                        <AiOutlineEdit /> Edit
                    </button>
                    <button
                        onClick={() => openDelete(row)}
                        aria-label={`Delete amendment ${row.filename}`}
                        style={buttonStyle("#dc2626")}
                    >
                        <AiOutlineDelete /> Delete
                    </button>
                </div>
            ),
            ignoreRowClick: true,
        },
    ], [currentPage, perPage, loading]);

    // Action button styles
    function buttonStyle(bgColor) {
        return {
            background: bgColor,
            color: "white",
            borderRadius: 6,
            border: "none",
            cursor: "pointer",
            padding: "6px 12px",
            fontSize: 13,
            display: "flex",
            alignItems: "center",
            gap: 4,
            userSelect: "none",
            fontWeight: 600,
        };
    }

    // Validation function
    function isFormValid() {
        return category && year && file && !fileError;
    }

    // Check if form has validation errors (only show after user tries to submit)
    function hasValidationErrors() {
        return showValidationErrors && (!category || !year || !file || !!fileError);
    }

    // File input change handler for upload form
    function onFileChange(e) {
        const selected = e.target.files[0];
        if (!selected) {
            setFile(null);
            setFileError("");
            return;
        }
        
        // Validate file using the validateFile function
        const validation = validateFile(selected);
        if (!validation.isValid) {
            setFile(null);
            setFileError(validation.errors.join('. '));
            if (fileInput.current) fileInput.current.value = "";
            return;
        }
        
        // File is valid
        setFile(selected);
        setFileError("");
    }

    // Upload Handler
    async function handleUpload(e) {
        e.preventDefault();
        setShowValidationErrors(true);
        if (!isFormValid()) return;

        // Check if amendment is already completed for this year
        // Find the year object to get the actual year number (not the UUID)
        const selectedYearObj = years.find(y => String(y.id) === String(year));
        if (selectedYearObj && completedYears.has(selectedYearObj.year)) {
            alert(`Amendment for ${selectedYearObj.year} has already been resolved. You cannot upload another amendment for this year.`);
            return;
        }

        setLoading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('category', category);  // Amendment or Notices (mandatory)
            formData.append('year', String(year || '')); // Send as UUID string, not integer
            
            // Also send year_number as fallback for already-filed years
            const selectedYearObj = years.find(y => String(y.id) === String(year));
            if (selectedYearObj && selectedYearObj.year) {
                formData.append('year_number', String(selectedYearObj.year));
            }
            
            formData.append('title', file.name);

            const result = await apiUploadAmendment(formData);
            
            // Refresh the amendments list
            const amendments = await apiGetAmendments();
            setData(amendments);
            
            // Reset form but keep year and category selected to allow multiple uploads
            // Only reset file to allow uploading another file for the same year
            setFile(null);
            setFileError("");
            setShowValidationErrors(false);
            if (fileInput.current) fileInput.current.value = "";
            
            // Note: We keep category and year selected to allow multiple uploads
            // User can manually change them if needed
            
        } catch (error) {
            console.error('Upload failed:', error);
            setFileError(error.message || 'Upload failed. Please try again.');
        }
        setLoading(false);
    }

    // Download file handler
    async function handleDownload(row) {
        setLoading(true);
        try {
            const response = await apiDownloadAmendment(row.id);
            
            const contentDisposition = response.headers.get('Content-Disposition');
            let filename = row.filename;
            
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="(.+)"/);
                if (filenameMatch) {
                    filename = filenameMatch[1];
                }
            }
            
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
        } catch (error) {
            console.error('Download failed:', error);
            alert('Failed to download file. Please try again.');
        }
        setLoading(false);
    }

    // Pagination handlers
    function handlePageChange(page) {
        setCurrentPage(page);
    }
    function handlePerRowsChange(newPerPage, page) {
        setPerPage(newPerPage);
        setCurrentPage(page);
    }

    // Edit modal handlers
    function openEdit(row) {
        setEditingDoc(row);
        setEditYear(row.year_id || row.year);
        setEditFile(null);
        setEditFileError("");
        if (editFileInput.current) editFileInput.current.value = "";
    }
    function closeEdit() {
        setEditingDoc(null);
        setEditFile(null);
        setEditFileError("");
    }
    function onEditFileChange(e) {
        const selected = e.target.files[0];
        if (!selected) {
            setEditFile(null);
            setEditFileError("");
            return;
        }
        
        const validation = validateFile(selected);
        if (!validation.isValid) {
            setEditFile(null);
            setEditFileError(validation.errors.join('. '));
            if (editFileInput.current) editFileInput.current.value = "";
            return;
        }
        
        setEditFile(selected);
        setEditFileError("");
    }
    
    // Save edited amendment
    async function saveEdit() {
        setLoading(true);
        try {
            const updateData = {
                year: parseInt(editYear),
                title: editFile ? editFile.name : editingDoc.filename,
                description: editingDoc.description || ''
            };
            
            await apiUpdateAmendment(editingDoc.id, updateData);
            
            if (editFile) {
                const formData = new FormData();
                formData.append('file', editFile);
                // Try to send as UUID string first, fallback to integer
                const selectedYearObj = years.find(y => String(y.id) === String(editYear) || String(y.year) === String(editYear));
                if (selectedYearObj) {
                    formData.append('year', String(selectedYearObj.id)); // Send UUID
                    formData.append('year_number', String(selectedYearObj.year)); // Send year number as fallback
                } else {
                    formData.append('year', String(editYear)); // Fallback to whatever editYear is
                }
                formData.append('title', editFile.name);
                
                await apiUploadAmendment(formData);
            }
            
            const amendments = await apiGetAmendments();
            setData(amendments);
            closeEdit();
        } catch (error) {
            console.error('Save failed:', error);
            alert('Failed to save changes. Please try again.');
        }
        setLoading(false);
    }

    // Delete handlers
    function openDelete(row) {
        setDeleteDoc(row);
    }
    function closeDelete() {
        setDeleteDoc(null);
    }
    async function confirmDelete() {
        if (!deleteDoc) return;
        setLoading(true);
        try {
            await apiDeleteAmendment(deleteDoc.id);
            const amendments = await apiGetAmendments();
            setData(amendments);
            closeDelete();
        } catch (error) {
            console.error('Delete failed:', error);
            alert('Failed to delete amendment. Please try again.');
        }
        setLoading(false);
    }

    // Sort handler
    function onSort(column, sortDirection) {
        setSortColumn(column.selector);
        setSortDirection(sortDirection);
    }

    // DataTable custom styles
    const customStyles = {
        headRow: {
            style: {
                backgroundColor: "#f3f4f6",
                fontWeight: 700,
                fontSize: 14,
                color: "#374151",
            },
        },
        rows: {
            style: {
                fontSize: 14,
                minHeight: "48px",
            },
        },
    };

    // Modal Component
    function Modal({ onClose, title, children }) {
        return (
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-title"
                style={{
                    position: "fixed",
                    top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: "rgba(0,0,0,0.25)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    zIndex: 10000,
                }}
                onClick={onClose}
            >
                <div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        backgroundColor: "white",
                        borderRadius: 12,
                        padding: 28,
                        maxWidth: 480,
                        width: "100%",
                        position: "relative",
                        userSelect: "none",
                        boxShadow: "0 5px 20px rgba(99, 102, 241, 0.3)",
                    }}
                >
                    <h3
                        id="modal-title"
                        style={{
                            fontWeight: 700,
                            marginBottom: 18,
                            fontSize: 21,
                            color: "#2d3393",
                        }}
                    >
                        {title}
                    </h3>
                    {children}
                    <button
                        aria-label="Close"
                        onClick={onClose}
                        style={{
                            position: "absolute",
                            top: 12,
                            right: 12,
                            background: "transparent",
                            border: "none",
                            fontSize: 24,
                            color: "#555",
                            cursor: "pointer",
                            userSelect: "none",
                        }}
                    >
                        <AiOutlineClose />
                    </button>
                </div>
            </div>
        );
    }

    return (
        <section style={{ userSelect: "none" }}>
            <h1
                style={{
                    fontSize: 38,
                    marginRight: 10,
                    color: "#4338ca",
                    verticalAlign: "middle",
                    filter: "drop-shadow(0px 1px 4px #b5beff77)",
                    marginBottom: 28,
                }}
                tabIndex={-1}
            >
                <AiOutlineUpload style={{ verticalAlign: "middle", marginRight: 10 }} />
                Amendment Uploads
            </h1>

            {/* Search Bar */}
            <div style={{ marginBottom: 20, display: "flex", alignItems: "center", gap: 12 }}>
                <div
                    style={{
                        position: "relative",
                        display: "flex",
                        alignItems: "center",
                        flexGrow: 1,
                        maxWidth: 400,
                    }}
                >
                    <AiOutlineSearch
                        style={{
                            position: "absolute",
                            left: 12,
                            color: "#9ca3af",
                            fontSize: 18,
                        }}
                    />
                    <input
                        type="text"
                        placeholder="Search by filename or year..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{
                            paddingLeft: 40,
                            paddingRight: searchTerm ? 40 : 12,
                            paddingTop: 10,
                            paddingBottom: 10,
                            borderRadius: 8,
                            border: "1px solid #ced4da",
                            fontSize: 14,
                            width: "100%",
                            outline: "none",
                        }}
                    />
                    {searchTerm && (
                        <button
                            onClick={() => setSearchTerm("")}
                            style={{
                                position: "absolute",
                                right: 8,
                                background: "transparent",
                                border: "none",
                                cursor: "pointer",
                                padding: 4,
                                display: "flex",
                                alignItems: "center",
                                color: "#666"
                            }}
                            title="Clear search"
                        >
                            <AiOutlineClose size={16} />
                        </button>
                    )}
                </div>
            </div>

            <form
                onSubmit={handleUpload}
                style={{
                    display: "flex",
                    gap: 12,
                    marginBottom: 28,
                    flexWrap: "wrap",
                    alignItems: "center",
                    maxWidth: 850,
                }}
            >
                <div style={{ display: "flex", flexDirection: "column", minWidth: 140 }}>
                    <select
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        required
                        style={{
                            padding: 8,
                            borderRadius: 6,
                            border: `1px solid ${(!category && showValidationErrors) ? "#dc2626" : "#ced4da"}`,
                            minWidth: 140,
                        }}
                    >
                        <option value="">Select Category</option>
                        <option value="amendment">Amendment</option>
                        <option value="notices">Notices</option>
                    </select>
                    {(!category && showValidationErrors) && (
                        <span style={{ color: "#dc2626", fontSize: "12px", marginTop: "2px" }}>
                            Category is required
                        </span>
                    )}
                </div>

                <div style={{ display: "flex", flexDirection: "column", minWidth: 120 }}>
                    <select
                        value={year}
                        onChange={(e) => setYear(e.target.value)}
                        required
                        style={{
                            padding: 8,
                            borderRadius: 6,
                            border: `1px solid ${(!year && showValidationErrors) ? "#dc2626" : "#ced4da"}`,
                            minWidth: 120,
                        }}
                    >
                        <option value="">Select Year</option>
                        {years.map((y) => {
                            // Only disable year if ALL amendments for that year have status "completed"
                            const isCompleted = completedYears.has(y.year);
                            return (
                                <option key={y.id} value={y.id} disabled={isCompleted}>
                                    {y.year} {isCompleted ? '(Resolved)' : ''}
                                </option>
                            );
                        })}
                    </select>
                    {(!year && showValidationErrors) && (
                        <span style={{ color: "#dc2626", fontSize: "12px", marginTop: "2px" }}>
                            Year is required
                        </span>
                    )}
                </div>

                <div style={{ display: "flex", flexDirection: "column", flexGrow: 1, minWidth: 180 }}>
                    <input
                        type="file"
                        onChange={onFileChange}
                        ref={fileInput}
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.png,.jpeg,.jpg,.svg,.heic,.heif"
                        style={{
                            flexGrow: 1,
                            minWidth: 180,
                            padding: 9,
                            borderRadius: 6,
                            border: `1px solid ${(!file && showValidationErrors) ? "#dc2626" : "#ced4da"}`,
                            fontSize: 14,
                            cursor: "pointer",
                        }}
                    />
                    {(!file && showValidationErrors) && (
                        <span style={{ color: "#dc2626", fontSize: "12px", marginTop: "2px" }}>
                            File is required
                        </span>
                    )}
                </div>

                <button
                    type="submit"
                    disabled={!isFormValid()}
                    style={{
                        padding: "7px 20px",
                        backgroundColor: isFormValid() ? "#4338ca" : "#a9b8f2",
                        borderRadius: 6,
                        border: "none",
                        color: "white",
                        fontWeight: 600,
                        cursor: isFormValid() ? "pointer" : "not-allowed",
                        userSelect: "none",
                        opacity: isFormValid() ? 1 : 0.6,
                        transition: "all 0.2s ease",
                    }}
                    title={
                        !category ? "Please select a category" :
                        !year ? "Please select a year" :
                        !file ? "Please select a file" :
                        fileError ? fileError :
                        "Upload amendment"
                    }
                >
                    <AiOutlineCloudUpload size={18} style={{ marginRight: 6 }} />
                    Upload
                </button>

                {fileError && (
                    <div style={{ color: "#dc2626", userSelect: "text", marginTop: 4, width: "100%" }}>
                        {fileError}
                    </div>
                )}
            </form>

            {/* DataTable */}
            <DataTable
                columns={columns}
                data={paginatedRows}
                highlightOnHover
                striped
                pagination
                paginationServer={false}
                paginationTotalRows={sortedData.length}
                paginationDefaultPage={currentPage}
                paginationPerPage={perPage}
                paginationRowsPerPageOptions={[10, 20, 30, 40, 50]}
                onChangeRowsPerPage={handlePerRowsChange}
                onChangePage={handlePageChange}
                sortServer={true}
                onSort={onSort}
                noHeader
                responsive
                persistTableHead
                customStyles={customStyles}
                progressPending={loading}
            />

            {/* Edit Modal */}
            {editingDoc && (
                <Modal onClose={closeEdit} title="Edit Amendment">
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            saveEdit();
                        }}
                        style={{ display: "flex", flexDirection: "column", gap: 18 }}
                    >
                        <div>
                            <strong>Category: </strong>
                            Amendment
                        </div>
                        <div>
                            <label style={{ fontWeight: 600, marginBottom: 6, display: "block" }}>
                                Year
                            </label>
                            <select
                                value={editYear}
                                onChange={(e) => setEditYear(e.target.value)}
                                required
                                style={{
                                    padding: 8,
                                    borderRadius: 6,
                                    border: "1px solid #ced4da",
                                    width: "100%",
                                    maxWidth: "300px"
                                }}
                            >
                                <option value="">Select Year</option>
                                {years.map((y) => (
                                    <option key={y.id} value={y.id}>
                                        {y.year}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <div style={{ marginBottom: 10 }}>
                                <strong>Current file: </strong>
                                <span style={{ color: "#256bb7" }}>{editingDoc.filename}</span>
                            </div>
                            <label
                                htmlFor="edit-file"
                                style={{ fontWeight: 600, marginBottom: 6, display: "block" }}
                            >
                                Select New File (Optional)
                            </label>
                            <input
                                id="edit-file"
                                type="file"
                                ref={editFileInput}
                                accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.png,.jpeg,.jpg,.svg,.heic,.heif"
                                onChange={onEditFileChange}
                                aria-label="Select new file"
                                style={{
                                    padding: "8px",
                                    borderRadius: "6px",
                                    border: "1px solid #ced4da",
                                    fontSize: "14px",
                                    cursor: "pointer",
                                    width: "100%",
                                    maxWidth: "300px"
                                }}
                            />
                            {editFile && (
                                <div style={{ marginTop: 5, color: "#256bb7" }}>
                                    New file selected: {editFile.name}
                                </div>
                            )}
                            {!!editFileError && (
                                <div style={{ color: "#dc2626" }}>{editFileError}</div>
                            )}
                        </div>
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: 15 }}>
                            <button 
                                type="button" 
                                onClick={closeEdit} 
                                style={{
                                    ...buttonStyle("#6c757d"),
                                    cursor: "pointer",
                                    opacity: 1
                                }}
                            >
                                Cancel
                            </button>
                            <button 
                                type="submit" 
                                disabled={loading}
                                style={{
                                    ...buttonStyle("#4338ca"),
                                    cursor: loading ? "not-allowed" : "pointer",
                                    opacity: loading ? 0.6 : 1
                                }}
                            >
                                {loading ? "Saving..." : "Save"}
                            </button>
                        </div>
                    </form>
                </Modal>
            )}

            {/* Delete Modal */}
            {deleteDoc && (
                <Modal onClose={closeDelete} title="Delete Amendment">
                    <div style={{ marginBottom: 20 }}>
                        Are you sure you want to delete <strong>{deleteDoc.filename}</strong>?
                    </div>
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 15 }}>
                        <button onClick={closeDelete} style={buttonStyle("#a9accb")}>
                            Cancel
                        </button>
                        <button onClick={confirmDelete} style={buttonStyle("#dc2626")}>
                            Delete
                        </button>
                    </div>
                </Modal>
            )}
        </section>
    );
}

